import { Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import { Tooltip } from 'antd'
import { InfoCircleFilled } from '@ant-design/icons'
import type React from 'react'
import { useMemo } from 'react'

interface IProps {
  title: string
  width?: string
}

export const FormLabels: React.FC<IProps> = ({ title, width = '500px' }) => {
  const display = useMemo(
    () => displayValues.find((v) => v.title === title)?.value || (() => <Texto appearance={'white'}>{title}</Texto>),
    [title]
  )
  const description = <Vertical className={'p-2 px-3'}>{display()}</Vertical>
  return (
    <Horizontal verticalCenter className={'p-2'}>
      <Texto className={'mr-2'}>{title}</Texto>
      <Tooltip title={description} overlayInnerStyle={{ minWidth: width }} placement={'topRight'}>
        <InfoCircleFilled />
      </Tooltip>
    </Horizontal>
  )
}

export const displayValues = [
  {
    title: 'Card Name',
    value: () => (
      <>
        <Texto appearance={'white'} className={'mb-1'}>
          Cards required for loading and unloading activities at Terminals.
        </Texto>
        <Texto appearance={'white'} className={'mb-1'}>
          Ex. Loading Card 90, Unloading Card 120
        </Texto>
      </>
    ),
  },
  {
    title: 'Activity',
    value: () => {
      const items = [
        { label: 'Loading', description: 'References loading activity (i.e. BOLs)' },
        { label: 'Unloading', description: 'References unloading activity (i.e. Backhauls)' },
      ]
      return (
        <>
          {items.map(({ label, description }) => (
            <Horizontal key={label} className={'mb-1'}>
              <Texto appearance={'white'} className={'mr-2'}>
                {label}:{' '}
              </Texto>
              <Texto appearance={'white'}>{description}</Texto>
            </Horizontal>
          ))}
        </>
      )
    },
  },
  {
    title: 'Certification Trigger',
    value: () => {
      const items = [
        { label: 'Automatic - Activity Based', description: 'Certification triggers based on days between Activity.' },
        { label: 'Manual - Date Based', description: 'Certification triggers based on days certification date.' },
        {
          label: 'Never Expires',
          description:
            'Will clear Days Valid and Warning Threshold and set Dispatch Impact to "Informational (No Impact)”.',
        },
      ]
      return (
        <>
          {items.map(({ label, description }) => (
            <Horizontal key={label} className={'mb-2'}>
              <Texto appearance={'white'} className={'mr-2'}>
                {label}:{' '}
              </Texto>
              <Texto appearance={'white'}>{description}</Texto>
            </Horizontal>
          ))}
        </>
      )
    },
  },
  {
    title: 'Days Valid',
    value: () => {
      const items = [
        { label: 'Automatic - Activity Based', description: 'Certification triggers based on days between Activity.' },
        { label: 'Manual - Date Based', description: 'Certification triggers based on days certification date.' },
        {
          label: 'Never Expires',
          description:
            'Will clear Days Valid and Warning Threshold and set Dispatch Impact to "Informational (No Impact)”.',
        },
      ]
      return (
        <>
          <Texto appearance={'white'} className={'mb-1'}>
            Based on Certification Trigger
          </Texto>
          {items.map(({ label, description }) => (
            <Horizontal key={label} className={'mb-1'}>
              <Texto appearance={'white'} className={'mr-2'}>
                {label}:{' '}
              </Texto>
              <Texto appearance={'white'}>{description}</Texto>
            </Horizontal>
          ))}
        </>
      )
    },
  },
  {
    title: 'Warning Threshold',
    value: () => <Texto appearance={'white'}>Days before Expiration to Trigger Warning</Texto>,
  },
  {
    title: 'Dispatch Impact',
    value: () => {
      const items = [
        {
          label: 'Alert',
          description: 'Alert on Driver Dispatch Driver Card and can dispatch',
        },
        {
          label: 'Alert Manual, Block Optimizer',
          description: `Alert on Driver Dispatch Driver Card of manually dispatched orders but blocks auto dispatching by Route Optimizer.  Missing card or certification date will alert dispatch.`,
        },
        {
          label: 'Block',
          description: 'Blocks dispatch with error message',
        },
        {
          label: 'Informational (No Impact)',
          description: 'No alert or blocking dispatch',
        },
      ]
      return (
        <>
          <Texto appearance={'white'} className={'mb-1'}>
            If orders or backhauls are attempted to be dispatched to a driver with no or an expired card or credential
            then:
          </Texto>
          {items.map((item) => (
            <Horizontal key={item.label} className={'mb-1'}>
              <Texto appearance={'white'} style={{ whiteSpace: 'nowrap' }} className={'mr-2'}>
                {item.label}:
              </Texto>
              <Texto appearance={'white'}>{item.description}</Texto>
            </Horizontal>
          ))}
        </>
      )
    },
  },

  {
    title: 'Carding Instructions (Optional)',
    value: () => (
      <>
        <Texto appearance={'white'} className={'mb-1'}>
          Enter specific carding instructions for a card at a terminal.
        </Texto>
      </>
    ),
  },
  {
    title: 'Activity Threshold',
    value: () => (
      <>
        <Texto appearance={'white'} className={'mb-1'}>
          Quantity of activities before certification is approved.
        </Texto>

        <Texto appearance={'white'} className={'mb-1'}>
          Only applies to "Automatic - Activity Based" Certification Triggers and must be &gt; 0
        </Texto>
        <Texto appearance={'white'}>
          If Activity Threshold is &gt; 1, then dispatched loads will Alert and adopt their Dispatch Impact after the
          Activity Threshold has been met.
        </Texto>
      </>
    ),
  },
]
